import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap } from '@/lib/gsap'
import { cn } from '@/lib/utils'

interface MagneticProps {
    children: ReactNode
    className?: string
    strength?: number
    /* Inner elements move further than the shell, which is what sells the pull
       rather than a flat slide. */
    innerSelector?: string
    innerStrength?: number
}

export const Magnetic: FC<MagneticProps> = ({
    children,
    className,
    strength = 0.32,
    innerSelector = '[data-magnetic-inner]',
    innerStrength = 0.55
}): ReactNode => {
    const scope = useRef<HTMLDivElement>(null)

    useGsap(() => {
        const el = scope.current

        if (!el || !window.matchMedia('(pointer: fine)').matches) {
            return
        }

        const inner = el.querySelector<HTMLElement>(innerSelector)

        const xTo = gsap.quickTo(el, 'x', { duration: 0.7, ease: 'elastic.out(1, 0.45)' })
        const yTo = gsap.quickTo(el, 'y', { duration: 0.7, ease: 'elastic.out(1, 0.45)' })
        const ixTo = inner ? gsap.quickTo(inner, 'x', { duration: 0.75, ease: 'elastic.out(1, 0.45)' }) : null
        const iyTo = inner ? gsap.quickTo(inner, 'y', { duration: 0.75, ease: 'elastic.out(1, 0.45)' }) : null

        const onMove = (event: PointerEvent): void => {
            const rect = el.getBoundingClientRect()
            const dx = event.clientX - (rect.left + rect.width / 2)
            const dy = event.clientY - (rect.top + rect.height / 2)

            xTo(dx * strength)
            yTo(dy * strength)
            ixTo?.(dx * (innerStrength - strength))
            iyTo?.(dy * (innerStrength - strength))
        }

        const onLeave = (): void => {
            xTo(0)
            yTo(0)
            ixTo?.(0)
            iyTo?.(0)
        }

        el.addEventListener('pointermove', onMove)
        el.addEventListener('pointerleave', onLeave)

        return () => {
            el.removeEventListener('pointermove', onMove)
            el.removeEventListener('pointerleave', onLeave)
        }
    }, scope, [strength, innerStrength, innerSelector])

    return (
        <div ref={scope} className={cn('inline-block', className)}>
            {children}
        </div>
    )
}
